import React, { useState } from 'react';
import { View, Text, StyleSheet, Pressable, ActivityIndicator, Platform, Image } from 'react-native';
import { useNavigate } from 'react-router-dom';
import WebScrollView from '../components/WebScrollView';
import { User, ChevronRight } from '../lib/icons';
import { useConversations } from '../hooks/useConversations';

const Conversations = () => {
  const navigate = useNavigate();
  const { conversations, loading, error } = useConversations();
  const [managerAvatarErrors, setManagerAvatarErrors] = useState<Set<string>>(new Set());
  
  // Filter out honeybot conversations
  const homeManagerConversations = conversations.filter(conv => conv.type !== 'honeybot');

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    if (diffInHours < 1) {
      const minutes = Math.floor(diffInMs / (1000 * 60));
      return minutes <= 1 ? 'Just now' : `${minutes}m ago`;
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return `${hours}h ago`;
    } else if (diffInDays < 7) {
      const days = Math.floor(diffInDays);
      return `${days}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getConversationName = (conversation: any) => {
    return conversation.home_manager?.full_name || 'Your Home Manager';
  };

  const getConversationSubtitle = (conversation: any) => {
    if (conversation.home?.address) {
      return `${conversation.home.address}, ${conversation.home.city}`;
    }
    return 'Home Manager';
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#000000" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Error loading conversations</Text>
        <Text style={styles.errorSubtext}>{error.message}</Text>
      </View>
    );
  }

  return (
    <WebScrollView style={styles.container}>
      <View style={styles.content}>
        <View style={styles.header}>
          <Text style={styles.title}>Messages</Text>
        </View>

        <View style={styles.conversationsList}>
          {homeManagerConversations.map((conversation) => (
            <Pressable
              key={conversation.id}
              style={({ pressed }) => [
                styles.conversationCard,
                pressed && styles.conversationCardPressed
              ]}
              onPress={() => navigate(`/conversations/${conversation.id}`)}
            >
              <View style={styles.avatarContainer}>
                <View style={[styles.avatar, styles.avatarManager]}>
                  {conversation.home_manager?.profile_photo_url && !managerAvatarErrors.has(conversation.id) ? (
                    <Image
                      source={{ uri: conversation.home_manager.profile_photo_url }}
                      style={styles.avatarImage}
                      onError={() => setManagerAvatarErrors(prev => new Set(prev).add(conversation.id))}
                    />
                  ) : (
                    <User size={24} color="#ffffff" />
                  )}
                </View>
                {conversation.homeowner_unread_count > 0 && (
                  <View style={styles.unreadBadge}>
                    <Text style={styles.unreadBadgeText}>
                      {conversation.homeowner_unread_count}
                    </Text>
                  </View>
                )}
              </View>

              <View style={styles.conversationInfo}>
                <View style={styles.conversationHeader}>
                  <Text style={styles.conversationName}>
                    {getConversationName(conversation)}
                  </Text>
                  {conversation.last_message_at && (
                    <Text style={styles.timestamp}>
                      {formatTimestamp(conversation.last_message_at)}
                    </Text>
                  )}
                </View>

                <Text style={styles.conversationSubtitle}>
                  {getConversationSubtitle(conversation)}
                </Text>

                {conversation.last_message_preview && (
                  <Text 
                    style={[
                      styles.messagePreview,
                      conversation.homeowner_unread_count > 0 && styles.messagePreviewUnread
                    ]}
                    numberOfLines={2}
                  >
                    {conversation.last_message_preview}
                  </Text>
                )}
              </View>

              <ChevronRight size={20} color="#9ca3af" style={styles.chevron} />
            </Pressable>
          ))}

          {homeManagerConversations.length === 0 && !loading && (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>No conversations yet</Text>
              <Text style={styles.emptyStateSubtext}>
                Start chatting with your home manager
              </Text>
            </View>
          )}
        </View>
      </View>
    </WebScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  content: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      maxWidth: 800,
      marginHorizontal: 'auto',
      width: '100%',
    }),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
    paddingHorizontal: 20,
  },
  errorText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#ef4444',
    marginBottom: 8,
  },
  errorSubtext: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
  },
  header: {
    paddingHorizontal: 16,
    paddingVertical: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: '#000000',
  },
  conversationsList: {
    paddingTop: 8,
  },
  conversationCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  conversationCardPressed: {
    backgroundColor: '#f3f4f6',
  },
  avatarContainer: {
    position: 'relative',
    marginRight: 12,
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
  },
  avatarManager: {
    backgroundColor: '#10b981',
  },
  unreadBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#ef4444',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 6,
  },
  unreadBadgeText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
  },
  conversationInfo: {
    flex: 1,
    marginRight: 8,
  },
  conversationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 2,
  },
  conversationName: {
    fontSize: 17,
    fontWeight: '600',
    color: '#000000',
  },
  timestamp: {
    fontSize: 13,
    color: '#9ca3af',
  },
  conversationSubtitle: {
    fontSize: 13,
    color: '#6b7280',
    marginBottom: 4,
  },
  messagePreview: {
    fontSize: 15,
    color: '#6b7280',
    lineHeight: 20,
  },
  messagePreviewUnread: {
    fontWeight: '600',
    color: '#000000',
  },
  chevron: {
    marginLeft: 4,
  },
  emptyState: {
    paddingVertical: 60,
    paddingHorizontal: 20,
    alignItems: 'center',
  },
  emptyStateText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#6b7280',
    marginBottom: 8,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#9ca3af',
    textAlign: 'center',
  },
});

export default Conversations;
